import React, { memo, useCallback, useContext, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import type { CellValue, IdType } from 'react-table'
import { mdiAlertCircleOutline } from '@mdi/js'
import FileSaver from 'file-saver'

import { Regular } from 'components/Typography/Labels'
import {
  MobileTableBlock,
  MobileTableBlockCell,
  MobileTableBlockHeader,
  MobileTableBlockRow,
} from 'components/Table/MobileBlock/styles'
import { KebabMenu, KebabMenuItem } from 'components/KebabMenu'
import IntlFormattedDate from 'app/i18n/IntlFormattedDate'
import { commonMessages } from 'app/i18n/messages'
import { fetchFile } from 'app/utils/fetchFile'
import { SpinnerCentered } from 'components/Loader'
import { ErrorIcon } from 'components/Table/Cell/DownloadButtonCell/styles'
import {
  ProfilesBody12,
  ProfilesBodyPrimary,
  ProfilesMobileBlockKebabItemWrapper,
} from './styles'
import ConfirmDeleteModal from 'components/Table/Cell/DeleteButtonCell/ConfirmDeleteModal'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { StyledProfileThumbnail } from 'features/profiles/components/ProfilesTable/styles'
import { PROFILES_HEADERS } from 'features/profiles/consts'
import { profilesPageMessages } from 'features/profiles/messages'
import { deleteProfileThunk } from 'features/profiles/slice'
import type { Profile } from 'features/profiles/types'
import { CurrentUserPermissionsContext } from 'features/auth/permissions/CurrentUserPermissionsContext'

interface ProfilesMobileBlockProps {
  values: Record<IdType<Profile>, CellValue>
}

export function ProfilesMobileBlock({ values }: ProfilesMobileBlockProps) {
  const { formatMessage } = useIntl()
  const { hasRootUserPermission } = useContext(CurrentUserPermissionsContext)

  const [isLoading, setIsLoading] = useState(false)
  const [showError, setShowError] = useState(false)
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState(false)

  const rootDomainId = useAppSelector(currentRootDomainIdSelector)
  const dispatch = useAppDispatch()

  const handleDeleteClick = async () => {
    if (rootDomainId) {
      await dispatch(
        deleteProfileThunk(rootDomainId, values[PROFILES_HEADERS.ID])
      )
      setIsConfirmDeleteModalOpen(false)
    }
  }

  const handleDownloadClick = useCallback(async () => {
    setIsLoading(true)
    try {
      const model = values[PROFILES_HEADERS.MODEL]
      const manufacturer = values[PROFILES_HEADERS.MANUFACTURER]
      const fileName = `${manufacturer}_${model}.tar.gz`
      const url = values[PROFILES_HEADERS.DOWNLOAD_LINK]
      const { data } = await fetchFile({
        url,
        responseType: 'blob',
        contentType: 'application/octet-stream',
      })

      FileSaver.saveAs(data, fileName)
    } catch (error) {
      setShowError(true)
      console.log('Error downloading file', error)
    } finally {
      setIsLoading(false)
    }
  }, [values])

  return (
    <>
      <MobileTableBlock>
        <MobileTableBlockRow>
          <MobileTableBlockHeader>
            <StyledProfileThumbnail
              url={values[PROFILES_HEADERS.THUMBNAIL]}
              alt="Profile thumbnail"
            />
            <ProfilesBodyPrimary>
              <strong>{values[PROFILES_HEADERS.MANUFACTURER]}</strong>
            </ProfilesBodyPrimary>
          </MobileTableBlockHeader>
          <KebabMenu>
            <KebabMenuItem
              title={
                <ProfilesMobileBlockKebabItemWrapper>
                  <FormattedMessage {...commonMessages.download} />
                  {isLoading && <SpinnerCentered size="16" />}
                  {showError && (
                    <ErrorIcon
                      path={mdiAlertCircleOutline}
                      title={formatMessage(commonMessages.commonError)}
                      description={formatMessage(commonMessages.commonError)}
                      size={0.8}
                    />
                  )}
                </ProfilesMobileBlockKebabItemWrapper>
              }
              onClick={handleDownloadClick}
            />
            {hasRootUserPermission && (
              <KebabMenuItem
                title={<FormattedMessage {...commonMessages.delete} />}
                onClick={() => setIsConfirmDeleteModalOpen(true)}
              />
            )}
          </KebabMenu>
        </MobileTableBlockRow>

        <MobileTableBlockRow>
          <MobileTableBlockCell>
            <ProfilesBody12>
              {formatMessage(profilesPageMessages.modelColumn)}
            </ProfilesBody12>
          </MobileTableBlockCell>
          <MobileTableBlockCell>
            <ProfilesBodyPrimary>
              {values[PROFILES_HEADERS.MODEL]}
            </ProfilesBodyPrimary>
          </MobileTableBlockCell>
        </MobileTableBlockRow>

        <MobileTableBlockRow>
          <MobileTableBlockCell>
            <ProfilesBody12>
              {formatMessage(profilesPageMessages.deviceClassColumn)}
            </ProfilesBody12>
          </MobileTableBlockCell>
          <MobileTableBlockCell>
            <ProfilesBodyPrimary>
              {values[PROFILES_HEADERS.DEVICE_CLASS]}
            </ProfilesBodyPrimary>
          </MobileTableBlockCell>
        </MobileTableBlockRow>

        <MobileTableBlockRow>
          <MobileTableBlockCell>
            <ProfilesBody12>
              {formatMessage(profilesPageMessages.uploadedColumn)}
            </ProfilesBody12>
          </MobileTableBlockCell>
          <MobileTableBlockCell>
            <ProfilesBodyPrimary>
              <IntlFormattedDate
                date={values[PROFILES_HEADERS.UPLOADED]}
                withTime
              />
            </ProfilesBodyPrimary>
          </MobileTableBlockCell>
        </MobileTableBlockRow>

        <MobileTableBlockRow>
          <MobileTableBlockCell>
            <ProfilesBody12>
              {formatMessage(profilesPageMessages.versionColumn)}
            </ProfilesBody12>
          </MobileTableBlockCell>
          <MobileTableBlockCell>
            <ProfilesBodyPrimary>
              {values[PROFILES_HEADERS.VERSION]}
            </ProfilesBodyPrimary>
          </MobileTableBlockCell>
        </MobileTableBlockRow>
      </MobileTableBlock>
      {isConfirmDeleteModalOpen && (
        <ConfirmDeleteModal
          isOpen
          handleDiscard={() => setIsConfirmDeleteModalOpen(false)}
          handleConfirm={handleDeleteClick}
          title={formatMessage(profilesPageMessages.deleteProfileModalTitle)}
        >
          <Regular>
            {formatMessage(profilesPageMessages.deleteProfileModalDescription, {
              model: values[PROFILES_HEADERS.MODEL],
              manufacturer: values[PROFILES_HEADERS.MANUFACTURER],
              br: <br />,
            })}
          </Regular>
        </ConfirmDeleteModal>
      )}
    </>
  )
}

export default memo(ProfilesMobileBlock)
